/**
 * Subscribes a Horizon `email-signup` form to a Klaviyo list from the browser.
 *
 * The element WRAPS the block's existing customer form rather than replacing it, so nothing
 * here binds through Horizon's `ref` system: the markup being enhanced belongs to the shared
 * block (password page, footer, generic section), and adding refs to it would change what
 * every other consumer renders. Standard form elements are located by selector instead, so
 * this file owns none of that markup.
 *
 * The form has no native destination any more. Shopify's own submit is cancelled by an inline
 * listener the block prints at parse time, so a submit that arrives before this module loads
 * does nothing at all instead of posting to Shopify and navigating the visitor away. This
 * module is therefore the ONLY path the address can travel.
 *
 * Klaviyo's client-side Create Client Subscription endpoint is designed for exactly this:
 * it authenticates with the PUBLIC api key (the same site id Klaviyo's own onsite script
 * exposes), so nothing secret reaches the page. Consent handling — including double opt-in
 * when the list requires it — is decided by Klaviyo from the list's own settings, so the
 * only thing this sends is `SUBSCRIBED`.
 *
 * Required data attributes:
 *   data-public-key  Klaviyo public API key / site id
 *   data-list-id     the list the address joins
 * Optional:
 *   data-source      value stored as Klaviyo's `custom_source` on the subscription
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

/**
 * Pinned so a later Klaviyo revision cannot change the payload contract under us.
 * @see https://developers.klaviyo.com/en/reference/create_client_subscription
 */
const KLAVIYO_REVISION = '2026-07-15';

class KlaviyoSignupForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    this.#form = this.querySelector('form');

    if (!this.#form || !this.dataset.publicKey || !this.dataset.listId) return;

    this.#controller = new AbortController();
    this.#form.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#form = null;
    this.#pending = false;
  }

  get #input() {
    return this.#form?.querySelector('input[type="email"]') ?? null;
  }

  get #button() {
    return this.#form?.querySelector('button') ?? null;
  }

  get #message() {
    return this.querySelector('[data-klaviyo-message]');
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    // Unconditional, and first: every path out of this handler must leave the browser with
    // nothing to submit, so no early return can hand the form back to Shopify.
    event.preventDefault();

    const email = this.#input?.value.trim();

    // The field is `required` and `type="email"`, so the browser blocks a submit event for
    // anything invalid. An empty value here means the field was detached, not skipped.
    if (!email) return;

    if (this.#pending) return;

    this.#subscribe(email);
  };

  /** @param {string} email */
  async #subscribe(email) {
    this.#setPending(true);

    // A second attempt starts from a clean slate: the region is announced, so leaving the
    // previous verdict on screen while a new request is in flight would be read as its result.
    this.#clearMessage();

    try {
      const response = await fetch(
        `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.publicKey ?? '')}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          signal: this.#controller?.signal,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.source || undefined,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.dataset.listId } },
              },
            },
          }),
        }
      );

      // Only a 2xx is a subscription: Klaviyo answers a valid request with 202 and nothing
      // else here means the address did not reach the list, so it must read as an error.
      if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);

      // A 202 carries no body: Klaviyo has accepted the address and, on a double opt-in
      // list, will only add it once the confirmation link is followed.
      this.#showMessage(this.dataset.successMessage, false);
      this.#form?.reset();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#showMessage(this.dataset.errorMessage, true);
    } finally {
      this.#setPending(false);
    }
  }

  /** @param {boolean} pending */
  #setPending(pending) {
    this.#pending = pending;

    const button = this.#button;

    if (button) {
      button.disabled = pending;
      button.setAttribute('aria-busy', String(pending));
    }
  }

  #clearMessage() {
    const message = this.#message;

    if (!message) return;

    message.textContent = '';
    message.hidden = true;
    message.classList.remove('email-signup__message--error');
  }

  /**
   * @param {string | undefined} text
   * @param {boolean} isError
   */
  #showMessage(text, isError) {
    const message = this.#message;

    if (!message || !text) return;

    message.textContent = text;
    message.classList.toggle('email-signup__message--error', isError);
    message.hidden = false;
  }
}

if (!customElements.get('klaviyo-signup-form')) {
  customElements.define('klaviyo-signup-form', KlaviyoSignupForm);
}
